/**
 * Hashing Utility for Device Fingerprinting
 *
 * Uses the Web Crypto API (SubtleCrypto) for SHA-256 hashing.
 * This is a native browser API — no external dependencies needed.
 */

/**
 * Generate a SHA-256 hash from a string input.
 * Uses the browser's native SubtleCrypto API for security and performance.
 *
 * @param input - The string to hash
 * @returns Hex-encoded SHA-256 hash
 */
export const generateSHA256Hash = async (input: string): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback: simple DJB2 hash for environments where SubtleCrypto is unavailable
    return fallbackHash(input);
  }
};

/**
 * DJB2 hash fallback for environments without SubtleCrypto.
 * This is not cryptographically secure but provides a consistent hash
 * for fingerprint identification purposes.
 */
const fallbackHash = (input: string): string => {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};
